import React, { useEffect, useState } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Modal, Form, Input, message, Checkbox } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DashboardOutlined, DatabaseOutlined, BarChartOutlined, FileTextOutlined, LogoutOutlined, UserOutlined, KeyOutlined, SafetyOutlined, LockOutlined } from '@ant-design/icons';

const { Header } = Layout;

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem('access_token');
  const [me, setMe] = useState(null);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [changePwdSubmitting, setChangePwdSubmitting] = useState(false);
  const [changePwdForm] = Form.useForm();

  const selectedMenuKey = (() => {
    try {
      const pathname = location?.pathname || '/';
      return pathname;
    } catch (e) {
      return location?.pathname || '/';
    }
  })();

  const openChangePassword = () => {
    setChangePwdOpen(true);
    try {
      changePwdForm.resetFields();
      changePwdForm.setFieldsValue({ logout_others: true });
    } catch (e) {
      // ignore
    }
  };

  const submitChangePassword = async () => {
    try {
      const values = await changePwdForm.validateFields();
      const oldPassword = values.old_password;
      const newPassword = values.new_password;
      const confirmPassword = values.confirm_password;
      if (newPassword !== confirmPassword) {
        message.error('两次输入的新密码不一致');
        return;
      }

      setChangePwdSubmitting(true);
      const api = await import('../services/api');
      await api.authAPI.changePassword(oldPassword, newPassword, !!values.logout_others);
      message.success('密码修改成功');
      setChangePwdOpen(false);
    } catch (e) {
      if (e?.errorFields) {
        return;
      }
      const msg = e?.response?.data?.detail || e?.message || '密码修改失败';
      message.error(String(msg));
    } finally {
      setChangePwdSubmitting(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const api = await import('../services/api');
        const resp = await api.authAPI.me();
        if (mounted) {
          setMe(resp.data);
        }
      } catch (e) {
        if (mounted) {
          setMe(null);
        }
      }
    };
    if (token) {
      run();
    }
    return () => {
      mounted = false;
    };
  }, [token]);

  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出登录',
      content: '确定要退出当前账号吗？',
      okText: '确认退出',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => {
        localStorage.removeItem('access_token');
        navigate('/login');
      },
    });
  };

  const isAdmin = !!me?.is_admin;
  const roleName = String(me?.role_name || '').toUpperCase();

  const adminMenuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: <Link to="/">仪表板</Link>,
    },
    {
      key: '/permission',
      icon: <KeyOutlined />,
      label: <Link to="/permission">权限管理</Link>,
    },
    {
      key: '/supervision',
      icon: <SafetyOutlined />,
      label: <Link to="/supervision">监管</Link>,
    },
    {
      key: '/master-data',
      icon: <DatabaseOutlined />,
      label: <Link to="/master-data">主数据管理</Link>,
    },
    {
      key: '/topic-records',
      icon: <FileTextOutlined />,
      label: <Link to="/topic-records">话题记录</Link>,
    },
    {
      key: '/trade-records',
      icon: <BarChartOutlined />,
      label: <Link to="/trade-records">交易记录</Link>,
    },
  ];

  const checkUserMenuItems = [
    {
      key: '/topic-records',
      icon: <FileTextOutlined />,
      label: <Link to="/topic-records">话题记录</Link>,
    },
    {
      key: '/trade-records',
      icon: <BarChartOutlined />,
      label: <Link to="/trade-records">交易记录</Link>,
    },
  ];

  const menuItems = isAdmin ? adminMenuItems : roleName === 'CHECK_USER' ? checkUserMenuItems : [];

  const userMenuItems = [
    {
      key: 'change_password',
      icon: <LockOutlined />,
      label: '修改密码',
      onClick: openChangePassword,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  // 如果没有token，不显示导航栏
  if (!token) {
    return null;
  }

  return (
    <Header
      style={{
        background: '#fff',
        padding: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 1px 4px rgba(0,21,41,.08)',
      }}
    >
      <div style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '24px' }}>AutoTrader 管理系统</div>

      {menuItems.length > 0 ? (
        <Menu
          mode="horizontal"
          selectedKeys={[selectedMenuKey]}
          items={menuItems}
          style={{
            flex: 1,
            borderBottom: 'none',
            marginLeft: '24px',
          }}
        />
      ) : (
        <div style={{ flex: 1 }} />
      )}

      <div style={{ marginRight: '24px' }}>
        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
          <Button type="text" icon={<Avatar icon={<UserOutlined />} />}>
            {me?.username || '用户'}
          </Button>
        </Dropdown>
      </div>

      <Modal title="修改密码" open={changePwdOpen} onOk={submitChangePassword} confirmLoading={changePwdSubmitting} onCancel={() => setChangePwdOpen(false)} okText="确认修改" cancelText="取消" destroyOnClose>
        <Form form={changePwdForm} layout="vertical" initialValues={{ logout_others: true }}>
          <Form.Item label="旧密码" name="old_password" rules={[{ required: true, message: '请输入旧密码' }]}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>

          <Form.Item
            label="新密码"
            name="new_password"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '新密码至少8位' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Form.Item label="确认新密码" name="confirm_password" rules={[{ required: true, message: '请再次输入新密码' }]}>
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Form.Item name="logout_others" valuePropName="checked">
            <Checkbox>踢下线其他设备</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </Header>
  );
};

export default Navbar;
